/**
 * enLanding — the public marketing page (buzzbitx.com) and the /playbook gift
 * page. Split out of `en.ts` (500-line ceiling); composed there with one spread.
 *
 * Positioning rule for this copy: BuzzBit X is for EVERY kind of business
 * owner — shops, clinics, coaches, course creators, SaaS, creators — never
 * "Shopify stores" alone. Only real, shipped capabilities; no invented numbers
 * (scene figures are labelled as sample data on screen).
 *
 * Keys live under `landing.*` beside the older `landing.connect…` lead-ads
 * keys in enIntegrations — distinct sub-namespaces, no collisions.
 */
import { enLandingScenes } from './enLandingScenes';
import { enLandingSections } from './enLandingSections';
import { enPlaybook } from './enPlaybook';

const enLandingPage = {
  // ── Legal (Israeli trader disclosure — content must stay as is) ─────────
  'landing.legal.owner': 'BuzzBit X',
  'landing.legal.line': 'BuzzBit X | 212 HaKormim Blvd., Arugot, Israel | support@buzzbitx.com',

  // ── Navbar ───────────────────────────────────────────────────────────────
  'landing.nav.product': 'Product',
  'landing.nav.useCases': 'Use cases',
  'landing.nav.faq': 'FAQ',
  'landing.nav.login': 'Log in',
  'landing.nav.getStarted': 'Get started free',
  'landing.nav.backToDashboard': 'Back to Dashboard',
  'landing.nav.openMenu': 'Open menu',
  'landing.nav.home': 'BuzzBit X home',
  'landing.nav.p.instagram': 'Instagram',
  'landing.nav.p.instagramDesc': 'Comment-to-DM',
  'landing.nav.p.whatsapp': 'WhatsApp',
  'landing.nav.p.whatsappDesc': 'Flows, templates, bookings and support',
  'landing.nav.p.messenger': 'Messenger',
  'landing.nav.p.messengerDesc': 'Instant replies and button flows',
  'landing.nav.p.email': 'Email',
  'landing.nav.p.emailDesc': 'Flows, campaigns and popups',
  'landing.nav.p.booking': 'Booking',
  'landing.nav.p.bookingDesc': 'Booking pages, reminders, BuzzCall video',

  // ── Hero ─────────────────────────────────────────────────────────────────
  'landing.hero.badge': 'Instagram · WhatsApp · Messenger · Email · Booking',
  'landing.hero.title1': 'Turn every comment, DM and message',
  'landing.hero.title2': 'into a customer',
  'landing.hero.cta': 'Get started free',
  'landing.hero.secondary': 'See it in action',

  // ── Channel tabs ─────────────────────────────────────────────────────────
  'landing.channels.title': 'Automate the channels your customers already use',
  'landing.channels.tablist': 'Channels',
  'landing.channels.instagram.1': 'Comment a keyword on any post and get an instant DM — a different keyword per post',
  'landing.channels.instagram.2': 'Automatic answers to story replies',
  'landing.channels.instagram.3': 'DM flows with buttons that qualify, tag and route every lead',
  'landing.channels.whatsapp.1': 'Flows with buttons and approved message templates',
  'landing.channels.whatsapp.2': 'Bookings, confirmations and reminders right in the chat',
  'landing.channels.whatsapp.3': 'Hand off to a human in your shared inbox at any step',
  'landing.channels.messenger.1': 'Instant answers to the questions you get every day',
  'landing.channels.messenger.2': 'Buttons that capture details and reserve a spot',
  'landing.channels.messenger.3': 'Every conversation lands on the same customer record',
  'landing.channels.email.1': 'Welcome, booking and win-back flows',
  'landing.channels.email.2': 'Campaigns to any segment of your customers',
  'landing.channels.email.3': 'Popups and forms that grow your list',

  // ── Flow showcase ────────────────────────────────────────────────────────
  'landing.flows.eyebrow': 'Watch the flows run',
  'landing.flows.title': 'Real flows, running on their own',
  'landing.flows.subtitle': 'Every scene below is a flow you can build in the visual builder — or install ready-made in one click.',
  'landing.flows.a.title': 'Comment → DM → gift',
  'landing.flows.a.body': 'Someone comments PLAYBOOK. They get a public reply, a DM, a follow check and your gift — in seconds.',
  'landing.flows.b.title': 'Book a call on WhatsApp',
  'landing.flows.b.body': 'Buttons, open slots in the customer’s local time, a BuzzCall video link and a reminder 24 hours before.',
  'landing.flows.c.title': 'Support that knows when to step aside',
  'landing.flows.c.body': 'Answers the common questions with buttons — and hands off to your team inbox when a human is needed.',
  'landing.flows.d.title': 'Email that follows up for you',
  'landing.flows.d.body': 'A welcome the moment they sign up, tips on day 3 — and opens and clicks you can watch come in.',
  'landing.flows.e.title': 'See what it brings in',
  'landing.flows.e.body': 'Reach, comments, DMs, bookings and revenue — together on one dashboard.',
  'landing.flows.loading': 'Loading scene…',

  // ── See it in action (video) ─────────────────────────────────────────────
  'landing.demo.eyebrow': 'See it in action',
  'landing.demo.title': 'Your business, on autopilot',
  'landing.demo.subtitle': 'Comments become DMs, DMs become bookings, bookings become customers — in one platform.',
  'landing.demo.videoLabel': 'Product demo: an Instagram comment, a WhatsApp booking, an email flow and the results dashboard',
} as const;

export const enLanding = { ...enLandingPage, ...enLandingSections, ...enLandingScenes, ...enPlaybook } as const;
